import _ from 'lodash';

type UnknownObject<T extends object> = Record<string | number | symbol, unknown> & { [K in keyof T]: unknown };

export function isObject<T extends object>(value: unknown): value is UnknownObject<T> {
    return typeof value === 'object' && value !== null;
}

export interface Disposable {
    dispose(): void;
}

// eslint-disable-next-line no-redeclare
/**
 * Represents a disposable object that can be cleaned up.
 */
export namespace Disposable {
    /**
     * Checks if the given argument is a Disposable object.
     * @param arg The argument to check.
     * @returns True if the argument is a Disposable object, false otherwise.
     */
    export function is(arg: unknown): arg is Disposable {
        return isObject<Disposable>(arg) && _.isFunction(arg.dispose);
    }

    /**
     * Creates a disposable object with the specified cleanup function.
     * @param func The cleanup function.
     * @returns The disposable object.
     */
    export function create(func: () => void): Disposable {
        return { dispose: func };
    }

    /**
     * A reference to a null disposable object.
     */
    export declare const NULL: Disposable;
}

/**
 * Ensures that every reference to {@link Disposable.NULL} returns a new object,
 * as sharing a disposable between multiple {@link DisposableCollection} can have unexpected side effects
 */
Object.defineProperty(Disposable, 'NULL', {
    configurable: false,
    enumerable: true,
    get(): Disposable {
        return { dispose: () => { } };
    }
});

export function disposableTimeout(...args: Parameters<typeof setTimeout>): Disposable {
    const handle = setTimeout(...args);
    return { dispose: () => clearTimeout(handle) };
}
